import React, { useState, useEffect } from 'react';
import { X, Search, Warehouse, Save, Loader2, MapPin, ArrowRightLeft, Settings, DollarSign, Calculator, Lock } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import UniversalLookup from './UniversalLookup';

const AddToolToStoreModal = ({ isOpen, onClose, toolnum, onRefresh }) => {
  const [storeData, setStoreData] = useState({ 
    location: '', 
    siteid: '', 
    issueunit: '',
    curbal: 0, 
    avgcost: 0 
  });
  
  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {} });
  const [loading, setLoading] = useState(false);

  const totalValue = (Number(storeData.curbal) * Number(storeData.avgcost)).toFixed(2);

  useEffect(() => {
    if (isOpen && toolnum) {
      const fetchToolDetails = async () => {
        try {
          // جلب بيانات الأداة من جدول الأدوات الجديد
          const res = await axios.get(`/api/tools/${toolnum}`);
          setStoreData(prev => ({
            ...prev,
            issueunit: res.data.issueunit || 'EACH'
          }));
        } catch (err) { console.error("Error fetching tool units:", err); }
      };
      fetchToolDetails();
    } else {
      setStoreData({ location: '', siteid: '', issueunit: '', curbal: 0, avgcost: 0 });
    }
  }, [isOpen, toolnum]);

  if (!isOpen) return null;

  const handleAssign = async () => {
    if (!storeData.location) return toast.error("Please select a Storeroom");
    setLoading(true);
    try {
      // إرسال البيانات للباكيند (نفس الـ Endpoint أو واحد مخصص للأدوات)
      await axios.post('/api/tool/inventory/assign', {
        toolnum: toolnum,
        ...storeData,
        curbal: Number(storeData.curbal),
        avgcost: Number(storeData.avgcost)
      });
      
      toast.success(`Tool ${toolnum} initialized in ${storeData.location}`);
      onRefresh(); 
      onClose();   
    } catch (err) {
      toast.error(err.response?.data?.detail || "Initialization failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200 uppercase font-sans tracking-tighter">
      <div className="bg-white w-[550px] shadow-2xl rounded-sm overflow-hidden border border-gray-300 animate-in zoom-in-95">
        
        {/* Header - Orange Theme for Tools */}
        <div className="bg-[#1a3353] text-white p-4 flex justify-between items-center border-b-4 border-b-orange-600">
          <div className="flex items-center gap-3">
            <Settings size={18} className="text-orange-400" />
            <span className="text-[11px] font-black tracking-widest italic">Initialize Tool in Storeroom</span>
          </div>
          <button onClick={onClose} className="hover:bg-red-600 p-1 rounded-full transition-colors text-white"><X size={18}/></button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6 bg-gray-50/50">
          
          <div className="flex items-center justify-between border-b border-gray-200 pb-3 bg-white p-3 rounded shadow-sm border-l-4 border-l-orange-600 font-black">
              <div className="flex flex-col">
                <label className="text-[8px] text-gray-400 italic">Tool Identification</label>
                <span className="text-[#1a3353] text-sm">{toolnum}</span>
              </div>
              <Settings size={20} className="text-gray-200" />
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="flex flex-col gap-1 border-b border-gray-300 focus-within:border-orange-600 transition-all">
                <label className="text-[9px] font-black text-gray-400 uppercase">Storeroom Location</label>
                <div className="flex items-center justify-between py-1">
                    <span className={`font-black text-[11px] ${storeData.location ? 'text-blue-900' : 'text-gray-300 italic'}`}>{storeData.location || 'SEARCH...'}</span>
                    <button onClick={() => setLookup({ isOpen: true, title: 'Storerooms', type: 'storerooms', field: 'location' })}>
                        <Search size={14} className="text-orange-600 hover:scale-110 transition-transform" />
                    </button>
                </div>
            </div>
            <div className="flex flex-col gap-1 border-b border-gray-100 bg-gray-100/50 p-1 rounded opacity-80">
                <label className="text-[9px] font-black text-gray-400 uppercase flex items-center gap-2"><Lock size={10}/> Site ID</label>
                <span className="font-black text-gray-600 text-[11px] py-1">{storeData.siteid || '---'}</span>
            </div>
          </div>

          {/* Valuation Section */}
          <div className="bg-white p-5 border rounded-sm shadow-sm space-y-5 border-t-4 border-t-green-600">
             <div className="flex items-center gap-2 text-green-700 font-black mb-2 uppercase text-[9px]">
                <DollarSign size={14}/> Opening Valuation
             </div>
             <div className="grid grid-cols-2 gap-8">
                <div className="flex flex-col border-b border-gray-200">
                    <label className="text-[8px] font-black text-gray-400 uppercase">Opening Qty</label>
                    <input type="number" className="font-black text-blue-900 outline-none py-2 bg-transparent text-lg font-mono" value={storeData.curbal} onChange={(e) => setStoreData({...storeData, curbal: e.target.value})} />
                </div>
                <div className="flex flex-col border-b border-gray-200 text-right">
                    <label className="text-[8px] font-black text-gray-400 uppercase">Unit Cost</label>
                    <input type="number" className="font-black text-blue-900 outline-none py-2 bg-transparent text-lg text-right font-mono" value={storeData.avgcost} onChange={(e) => setStoreData({...storeData, avgcost: e.target.value})} />
                </div>
             </div>
             <div className="bg-green-50 p-3 rounded flex justify-between items-center border border-green-100">
                <span className="text-[9px] font-black text-green-800 uppercase italic">Total Value:</span>
                <span className="text-xl font-black text-green-700 font-mono tracking-tighter">{totalValue} EGP</span>
             </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 bg-gray-100 border-t flex justify-end gap-3 px-8">
          <button onClick={onClose} className="px-6 py-2 text-[10px] font-black text-gray-500 uppercase">Cancel</button>
          <button onClick={handleAssign} disabled={loading} className="bg-[#1a3353] hover:bg-orange-700 text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 shadow-lg transition-all active:scale-95 disabled:opacity-50 uppercase tracking-widest">
            {loading ? <Loader2 className="animate-spin" size={14}/> : <Save size={14} />}
            Finalize Tool Stock
          </button>
        </div>

        <UniversalLookup {...lookup} onClose={() => setLookup({ ...lookup, isOpen: false })}
          onSelect={(row) => {
            const locValue = row.location || row.location_id;
            const siteValue = row.site_id || row.siteid;
            if (lookup.field === 'location') {
                setStoreData(prev => ({ ...prev, location: locValue, siteid: siteValue }));
            }
            setLookup({ ...lookup, isOpen: false });
          }}
        />
      </div>
    </div>
  );
};

export default AddToolToStoreModal;